/**
 * Borde arrastrable para un panel de ancho variable.
 *
 * El ancho se escribe en una variable CSS sobre `:root`, no sobre el panel:
 * lo leen dos lugares —el ancho del panel y el desplazamiento del área
 * principal— y desde `:root` no hay forma de que queden desfasados. Se recuerda
 * entre sesiones.
 */
export interface EdgeResizerOptions {
  /**
   * De qué lado del panel está el agarre. El sidebar lo tiene a la derecha
   * (arrastrar hacia la derecha ensancha); el panel lateral, a la izquierda,
   * donde el mismo gesto lo angosta. Es lo único que cambia entre los dos: el
   * ancho, el tope y el gesto de cierre se miden igual de los dos lados.
   */
  side?: "left" | "right";
  /** Variable CSS con el ancho, declarada en `:root`. */
  cssVar: string;
  /** Clave de localStorage donde se recuerda. */
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /** Píxeles que hay que dejarle siempre al área principal. */
  reserve: number;
  /**
   * Colapso por arrastre: pasado el ancho mínimo el panel se va atenuando en
   * proporción a cuánto más se lo empuja hacia la izquierda, y al llegar al
   * borde de la pantalla queda armado — soltar ahí lo cierra.
   *
   * No hay reloj: el gesto es distancia pura, así que el usuario ve cuánto le
   * falta y puede volver atrás en cualquier momento. Achicar de más no cierra
   * nada mientras no se llegue al fondo. Sin `onCollapse` el gesto no existe y
   * el borde sólo topa en el mínimo.
   */
  onCollapse?: () => void;
  /** Ancho (ficticio, por debajo del mínimo) donde el gesto queda armado. */
  commitAt?: number;
}

/** Clase que marca "empujando hacia el cierre" sobre el panel. */
const PENDING_CLASS = "is-collapse-pending";
/** Clase del tramo final: soltar acá cierra. */
const ARMED_CLASS = "is-collapse-armed";
/** Variable CSS 0→1 con cuánto se avanzó hacia el cierre. */
const PROGRESS_VAR = "--collapse-progress";

export class EdgeResizer {
  /** El agarre; el dueño lo mete en el panel donde quiera. */
  readonly el: HTMLElement;

  constructor(private opts: EdgeResizerOptions) {
    this.el = document.createElement("div");
    this.el.className = "edge-resize" + (opts.side === "left" ? " on-left" : "");
    this.el.addEventListener("pointerdown", this.onPointerDown);
    // Al achicarse la ventana, un ancho guardado de antes puede no dejar lugar
    // al área principal; el tope depende de `innerWidth`, así que se reacota.
    window.addEventListener("resize", this.onWindowResize);
    this.apply(this.stored());
  }

  /** Ancho pedido, acotado a lo razonable y a lo que deje ver el contenido. */
  private clamp(px: number): number {
    const room = Math.max(this.opts.minWidth, window.innerWidth - this.opts.reserve);
    return Math.round(Math.min(this.opts.maxWidth, room, Math.max(this.opts.minWidth, px)));
  }

  /** Ancho actual, leído de la variable CSS. */
  width(): number {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(this.opts.cssVar);
    return this.clamp(parseFloat(raw) || this.opts.defaultWidth);
  }

  private apply(px: number): void {
    document.documentElement.style.setProperty(this.opts.cssVar, `${this.clamp(px)}px`);
  }

  private stored(): number {
    try {
      return Number(localStorage.getItem(this.opts.storageKey)) || this.opts.defaultWidth;
    } catch {
      return this.opts.defaultWidth;
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(this.opts.storageKey, String(this.width()));
    } catch {
      // Modo privado / almacenamiento lleno: el ancho vale para esta sesión.
    }
  }

  private onWindowResize = (): void => this.apply(this.width());

  /**
   * El arrastre se escucha en `window`, no en el agarre.
   *
   * Escuchándolo en el agarre, soltar el botón afuera de esa tira de 6px no
   * terminaba el gesto: como el borde sigue al puntero, los `pointermove` le
   * seguían llegando y el panel quedaba pegado al mouse hasta el próximo click.
   * `window` ve el `pointerup` sin importar dónde caiga. Se filtra por
   * `pointerId` para que un segundo dedo no mueva el borde del primero.
   */
  private onPointerDown = (e: PointerEvent): void => {
    e.preventDefault();
    this.el.classList.add("is-dragging");
    // Mientras se arrastra, el cursor y el bloqueo de selección valen para toda
    // la ventana: el puntero se va a salir del borde de 6px enseguida.
    document.body.classList.add("is-col-resizing");

    const pointerId = e.pointerId;
    const startX = e.clientX;
    const startWidth = this.width();

    // Panel al que pertenece el agarre: es el que se va atenuando a medida que
    // el arrastre avanza hacia el cierre.
    const panel = this.el.parentElement;
    // El tramo de atenuación va del ancho mínimo hasta `commitAt`, un ancho
    // ficticio pegado al borde de la pantalla: llevar el cursor "hasta la punta
    // de la izquierda" es exactamente recorrerlo entero.
    const commitAt = this.opts.commitAt ?? 24;
    const span = Math.max(1, this.opts.minWidth - commitAt);
    let armed = false;

    /** Pinta el avance hacia el cierre: 0 = ancho mínimo, 1 = armado. */
    const setProgress = (p: number): void => {
      if (!panel) return;
      panel.style.setProperty(PROGRESS_VAR, String(p));
      panel.classList.toggle(PENDING_CLASS, p > 0);
      armed = p >= 1;
      panel.classList.toggle(ARMED_CLASS, armed);
    };

    const onMove = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return;
      // Con el agarre a la izquierda el panel crece hacia donde el puntero
      // RETROCEDE, así que el desplazamiento entra con el signo dado vuelta.
      const grow = this.opts.side === "left" ? startX - ev.clientX : ev.clientX - startX;
      const raw = startWidth + grow;
      this.apply(raw);
      // El ancho ya está topado en el mínimo: lo que mide el avance es cuánto se
      // pasó el puntero, no cuánto se movió el borde.
      if (this.opts.onCollapse) {
        setProgress(Math.min(1, Math.max(0, (this.opts.minWidth - raw) / span)));
      }
    };

    /**
     * Cierra el gesto. `collapse` llega en true al soltar dentro del tramo
     * armado —el panel ya se ve casi apagado, soltar cumple lo que promete—;
     * soltar antes del fondo sólo deja el panel en su ancho mínimo.
     */
    const finish = (collapse: boolean): void => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      setProgress(0);
      panel?.style.removeProperty(PROGRESS_VAR);
      this.el.classList.remove("is-dragging");
      document.body.classList.remove("is-col-resizing");
      // Se guarda el ancho previo al gesto: al reabrir, el panel vuelve como
      // estaba y no pegado al mínimo donde se lo cerró.
      if (collapse) this.apply(startWidth);
      this.persist();
      if (collapse) this.opts.onCollapse?.();
    };

    const onUp = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return;
      finish(armed);
    };

    // Un gesto cancelado por el sistema (scroll nativo, gesto del trackpad) no
    // es una intención de cerrar: termina el arrastre y deja el panel abierto.
    const onCancel = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return;
      finish(false);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  };

  dispose(): void {
    window.removeEventListener("resize", this.onWindowResize);
    this.el.remove();
  }
}
